import { useEffect, useId, useRef, useState } from "react";
import { absFile } from "@/lib/api";

export function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

export const Asterisk = ({ className = "", size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={`shrink-0 ${className}`} aria-hidden="true">
    <path d="M12 2.5v19M3.8 7.2l16.4 9.6M3.8 16.8l16.4-9.6" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
  </svg>
);

export const Heart = ({ className = "", size = 40 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} className={className} fill="none" aria-hidden="true">
    <path d="M24 41c-9-6-17-12-18.5-20C4.6 15 8.6 10 14.5 10c4 0 7 3 9 6.5 2-4 5.5-7.2 10.4-6.3 6 1.1 8.8 6.6 7 12.3C38.7 29.6 31.6 35.6 24 41z" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 44c2 .5 6 .4 8-.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const Squiggle = ({ className = "", width = 110 }) => (
  <svg viewBox="0 0 110 50" width={width} className={`shrink-0 ${className}`} fill="none" aria-hidden="true">
    <path d="M4 40c18-30 34 10 52-12s30-16 44-14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    <path d="M90 6l12 8-12 8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function Stamp({ text = "OFFICIALLY GETTING MARRIED • 03.12.2026 • ", center = "NiGi", color = "#A62D58", className = "" }) {
  const id = "c" + useId().replace(/[^a-zA-Z0-9]/g, "");
  return (
    <div className={`${className.includes("absolute") ? "" : "relative"} ${className}`} role="img" aria-label="Officially getting married stamp">
      <svg viewBox="0 0 200 200" className="w-full h-full spin-slow">
        <defs><path id={id} d="M100,100 m-80,0 a80,80 0 1,1 160,0 a80,80 0 1,1 -160,0" /></defs>
        <text fill={color} style={{ fontFamily: "Manrope", fontWeight: 800, fontSize: 13.5, letterSpacing: 2.2 }}>
          <textPath href={`#${id}`}>{text}</textPath>
        </text>
      </svg>
      <div className="absolute inset-[24%] rounded-full flex items-center justify-center" style={{ border: `2px dashed ${color}` }}>
        <span className="font-display italic text-2xl md:text-3xl" style={{ color }}>{center}</span>
      </div>
    </div>
  );
}

export function ClockFace({ active, color = "#C6A46C", className = "" }) {
  return (
    <svg viewBox="0 0 200 200" className={className} aria-hidden="true">
      <circle cx="100" cy="100" r="94" fill="none" stroke={color} strokeWidth="1.5" />
      <circle cx="100" cy="100" r="85" fill="none" stroke={color} strokeWidth="0.7" strokeDasharray="1.5 4" />
      {Array.from({ length: 12 }).map((_, i) => (
        <line key={i} x1="100" y1="16" x2="100" y2={i % 3 === 0 ? 32 : 24} stroke={color} strokeWidth={i % 3 === 0 ? 3 : 1.4} transform={`rotate(${i * 30} 100 100)`} />
      ))}
      <g className="clock-hand" style={{ transform: `rotate(${active ? 335.5 : 0}deg)` }}>
        <line x1="100" y1="104" x2="100" y2="54" stroke={color} strokeWidth="5" strokeLinecap="round" />
      </g>
      <g className="clock-hand" style={{ transform: `rotate(${active ? 426 : 0}deg)`, transitionDelay: ".15s" }}>
        <line x1="100" y1="108" x2="100" y2="28" stroke={color} strokeWidth="2.6" strokeLinecap="round" />
      </g>
      <circle cx="100" cy="100" r="6" fill={color} />
    </svg>
  );
}

export function PhotoFrame({ src, caption, initial = "N&G", tone = "#F7C9D0", rotate = 0, ratio = "aspect-[4/5]", className = "", tape = true, testid }) {
  return (
    <figure data-testid={testid} className={`polaroid ${className.includes("absolute") ? "" : "relative"} bg-white p-3 pb-14 shadow-[0_18px_40px_-12px_rgba(37,33,34,0.35)] ${className}`} style={{ "--r": `${rotate}deg` }}>
      {tape && <span className="tape" />}
      <div className={`w-full overflow-hidden ${ratio}`} style={{ background: tone }}>
        {src ? (
          <img src={absFile(src)} alt={caption || "Niyati and Girish"} loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-[#252122]">
            <span className="font-display italic text-5xl md:text-6xl opacity-80">{initial}</span>
            <span className="font-hand text-sm mt-2 opacity-60">photo coming soon</span>
          </div>
        )}
      </div>
      {caption && <figcaption className="font-hand absolute bottom-3 inset-x-3 text-center text-[#252122]/80 text-lg leading-tight">{caption}</figcaption>}
    </figure>
  );
}

export function Marquee({ items, className = "", textClass = "font-display italic text-2xl md:text-4xl" }) {
  const row = (p) => items.map((t, i) => (
    <span key={`${p}${i}`} className="flex items-center gap-8 pr-8">
      <span className={`whitespace-nowrap ${textClass}`}>{t}</span>
      <Asterisk size={20} />
    </span>
  ));
  return (
    <div className={`overflow-hidden py-4 ${className}`} aria-hidden="true">
      <div className="marquee-track items-center">{row("a")}{row("b")}</div>
    </div>
  );
}

export const Ticket = ({ children, className = "", notch = "#FFF9F0", style }) => (
  <span className={`ticket kicker ${className}`} style={{ "--notch": notch, ...style }}>{children}</span>
);

export const Mug = ({ className = "" }) => (
  <svg viewBox="0 -14 124 124" className={className} role="img" aria-label="World's best couple mug">
    <path d="M38 -2c0-6 6-6 6-12M56 -2c0-6 6-6 6-12M74 -2c0-6 6-6 6-12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" opacity=".45" />
    <path d="M18 8h76v68a14 14 0 0 1-14 14H32a14 14 0 0 1-14-14z" fill="#FFF9F0" stroke="#252122" strokeWidth="3" />
    <path d="M94 24h8a15 15 0 0 1 0 30h-8" fill="none" stroke="#252122" strokeWidth="3" />
    <text x="56" y="42" textAnchor="middle" style={{ font: "800 9.5px Manrope" }} fill="#252122">WORLD'S BEST</text>
    <text x="56" y="58" textAnchor="middle" style={{ font: "800 13px Manrope" }} fill="#252122">COUPLE</text>
  </svg>
);

export function Bunting({ colors, className = "" }) {
  const flags = Array.from({ length: 13 }).map((_, i) => {
    const x = i * 31 + 4;
    const y = (t) => 4 + 40 * t * (1 - t);
    return { x, y1: y(x / 400), y2: y((x + 24) / 400), c: colors[i % colors.length] };
  });
  return (
    <svg viewBox="0 0 400 44" preserveAspectRatio="none" className={className} aria-hidden="true">
      <path d="M0 4 Q200 44 400 4" stroke="currentColor" strokeWidth="1.2" fill="none" />
      {flags.map((f, i) => (
        <path key={i} d={`M${f.x} ${f.y1} L${f.x + 24} ${f.y2} L${f.x + 12} ${(f.y1 + f.y2) / 2 + 20} Z`} fill={f.c} />
      ))}
    </svg>
  );
}

export const FloralVine = ({ className = "", color = "#6F8B6B" }) => (
  <svg viewBox="0 0 300 60" className={className} fill="none" aria-hidden="true">
    <path d="M2 40 C 60 10, 100 60, 150 30 S 240 10, 298 34" stroke={color} strokeWidth="1.4" />
    {[30, 80, 130, 180, 230, 270].map((x, i) => (
      <g key={x} transform={`translate(${x} ${i % 2 ? 22 : 36}) rotate(${i % 2 ? -30 : 30})`}>
        <ellipse rx="9" ry="4" fill={color} opacity=".55" />
      </g>
    ))}
    {[60, 160, 250].map((x) => (
      <g key={x} transform={`translate(${x} 30)`}>
        {[0, 72, 144, 216, 288].map((r) => <ellipse key={r} rx="3.5" ry="7" transform={`rotate(${r}) translate(0 -6)`} fill="#F7C9D0" />)}
        <circle r="2.5" fill="#C6A46C" />
      </g>
    ))}
  </svg>
);

export const DecoCorner = ({ className = "", color = "#C6A46C" }) => (
  <svg viewBox="0 0 80 80" className={className} fill="none" aria-hidden="true">
    {[20, 34, 48, 62].map((r) => <path key={r} d={`M0 ${r} A ${r} ${r} 0 0 0 ${r} 0`} stroke={color} strokeWidth="1" />)}
    {[0, 15, 30, 45, 60, 75, 90].map((a) => (
      <line key={a} x1="0" y1="0" x2={70 * Math.cos((a * Math.PI) / 180)} y2={70 * Math.sin((a * Math.PI) / 180)} stroke={color} strokeWidth=".6" opacity=".6" />
    ))}
  </svg>
);

export const CandleGlow = ({ className = "" }) => (
  <div className={`pointer-events-none ${className}`} aria-hidden="true">
    {[["12%", "18%", 220], ["78%", "12%", 160], ["62%", "70%", 260], ["20%", "80%", 140]].map(([l, t, s], i) => (
      <span key={i} className="flicker absolute rounded-full" style={{ left: l, top: t, width: s, height: s, animationDelay: `${i * 0.7}s`, background: "radial-gradient(circle, rgba(242,165,65,0.38) 0%, rgba(242,165,65,0) 70%)" }} />
    ))}
  </div>
);

export function EventDecor({ slug, t, page = false }) {
  const frame = page ? "hidden md:block" : "";
  if (slug === "mehendi") return <Bunting colors={t.palette} className="absolute top-0 inset-x-0 w-full h-10 text-[#3B0A2A]/60" />;
  if (slug === "wedding") return <span className={`absolute inset-3 border pointer-events-none ${frame}`} style={{ borderColor: `${t.accent2}80` }} />;
  if (slug === "reception") return (
    <div className={frame}>
      <span className="absolute inset-3 border pointer-events-none" style={{ borderColor: `${t.accent}66` }} />
      <span className="absolute inset-5 border pointer-events-none" style={{ borderColor: `${t.accent}33` }} />
      <DecoCorner className="absolute top-5 left-5 w-14 h-14" color={t.accent} />
      <DecoCorner className="absolute bottom-5 right-5 w-14 h-14 rotate-180" color={t.accent} />
    </div>
  );
  if (slug === "sangeet") return <CandleGlow className="absolute inset-0" />;
  return null;
}

export function Confetti({ fire }) {
  const [pieces, setPieces] = useState([]);
  useEffect(() => {
    if (!fire) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const colors = ["#FF6657", "#F7B733", "#3458B5", "#A62D58", "#A9C79D", "#F7C9D0"];
    setPieces(Array.from({ length: 90 }, (_, i) => ({
      i, left: Math.random() * 100, dx: (Math.random() - 0.5) * 220, delay: Math.random() * 0.6,
      dur: 2.2 + Math.random() * 1.6, color: colors[i % colors.length], w: 6 + Math.random() * 8, round: Math.random() > 0.6,
    })));
    const t = setTimeout(() => setPieces([]), 4800);
    return () => clearTimeout(t);
  }, [fire]);
  if (!pieces.length) return null;
  return (
    <div className="fixed inset-0 pointer-events-none z-[70] overflow-hidden" aria-hidden="true" data-testid="confetti">
      {pieces.map((p) => (
        <span key={p.i} className="confetti" style={{ left: `${p.left}%`, background: p.color, width: p.w, height: p.round ? p.w : p.w * 0.45, borderRadius: p.round ? "50%" : "1px", "--dx": `${p.dx}px`, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s` }} />
      ))}
    </div>
  );
}

export const Wordmark = ({ className = "", tone = "#252122", accent = "#FF6657" }) => (
  <span className={`inline-flex items-start gap-1.5 ${className}`} style={{ color: tone }}>
    <span className="font-display italic font-medium leading-none">Ni<span style={{ color: accent }}>G</span>i</span>
    <span className="font-ui text-[9px] font-extrabold tracking-[0.18em] leading-[1.1] mt-0.5 opacity-80">11<br />11</span>
  </span>
);
